import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from "react-router-dom";
import Footer from './Footer.js';
import LoadingAnimation from './LoadingAnimation.js';
import * as utils from '../common/Utils.js';
import { useConfig } from '../contexts/ConfigContext.js';
import { useLeagueData } from '../contexts/LeagueDataContext.js';

import { collection, getDocs, addDoc, query, where, Timestamp } from "firebase/firestore";
import { db } from '../firebase.js';

const ReturningPlayer = () => {
  const navigate = useNavigate();
  const config = useConfig();
  const CURRENT_SEASON = config.currentSeason;
  const { players, dataLoaded } = useLeagueData(['players']);
  const [dataReady, setDataReady] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredPlayers, setFilteredPlayers] = useState([]);
  const [signups, setSignups] = useState([]);
  const [savingPlayerID, setSavingPlayerID] = useState(null);
  // Ref (not state) so a rapid second tap is blocked before React re-renders
  const isSavingRef = useRef(false);

  useEffect(() => {
    if (!config.configLoaded) return;
    getSignups();
  }, [config.configLoaded, CURRENT_SEASON]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!dataReady || !dataLoaded) return;

    const filtered = searchTerm.trim() === ''
    ? [] // Show no results if searchTerm is blank
    : players.filter(player =>
        `${player.firstName} ${player.lastName}`.toLowerCase().includes(searchTerm.toLowerCase())
      );

    setFilteredPlayers(filtered);
  }, [searchTerm, dataReady, dataLoaded, players]);

  const getSignups = async () => {
    const signupsSnapshot = await getDocs(collection(db, "signups"));
    const signups = []
    signupsSnapshot.forEach((doc) => {
      const id = doc.id
      const signup = doc.data()
      signup.id = id
      signups.push(signup)
    });

    const thisSeasonSignups = signups.filter((signup) => signup.season == CURRENT_SEASON)

    let signupsSortedNewestToOldest = thisSeasonSignups.sort(function(s1,s2){
      let s1_date = new Date(s1.created_at.seconds * 1000 + s1.created_at.nanoseconds / 1000000)
      let s2_date = new Date(s2.created_at.seconds * 1000 + s2.created_at.nanoseconds / 1000000)
      return s2_date - s1_date;
    })
    setSignups(signupsSortedNewestToOldest)

    setDataReady(true);
  }

  const savePlayer = async (playerID) => {
    if (isSavingRef.current) return;
    isSavingRef.current = true;
    setSavingPlayerID(playerID);

    try {
      // Check Firestore right before writing, the list loaded with the page can
      // be stale (other tab/device). Filter season loosely since older records
      // may store it as a number or a string.
      const existing = await getDocs(query(collection(db, "signups"), where("player_id", "==", playerID)));
      const alreadySignedUp = existing.docs.some((d) => d.data().season == CURRENT_SEASON);

      if (!alreadySignedUp) {
        await addDoc(collection(db, "signups"), {
          created_at: Timestamp.fromDate(new Date()),
          player_id: playerID,
          status: 'returning',
          season: CURRENT_SEASON,
          paid: false
        });
      }

      navigate(`/signup/all/${playerID}`)
    } catch (err) {
      console.error('Failed to sign up player:', err);
      alert('Something went wrong signing up. Please try again.');
      isSavingRef.current = false;
      setSavingPlayerID(null);
    }
  }

  function playerSignedUp (player_id) {
    return signups.map((signup) => signup.player_id).includes(player_id);
  }

  function displayPlayerSearch () {
    return (
      <div>
        <label className="signup-label" htmlFor="playerSearch">Find Your Name</label>
        <input type="text" className="signup-input" id="playerSearch"
          placeholder="Search for your name..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}>
        </input>
        {searchTerm.trim() === '' ? ( <></>
        ) : (
        filteredPlayers.length > 0 ? (
          <div className="signup-list">
            {filteredPlayers.map((player) => (
              <div className="signup-list-row" key={player.id}>
                <p className="signup-list-name">
                  {player.firstName} {player.lastName}
                </p>
                { playerSignedUp(player.id) ?
                  <button className="signup-btn" type="button" disabled>Signed Up</button> :
                  <button className="signup-btn" type="button" onClick={() => savePlayer(player.id)} disabled={savingPlayerID !== null}>
                    {savingPlayerID === player.id ? 'Signing Up...' : 'Sign Up ›'}
                  </button>
                }
              </div>
            ))}
          </div>
        ) : (
          <p className="signup-empty">No players found. New to the league? <a href="/signup/new">Sign up as a new player</a>.</p>
            )
        )}
      </div>
    );
  }

  if (dataReady == false) {
    return (
      <div className="HomepageUpdated signup-flow">
        <LoadingAnimation />
      </div>
    )
  }
  else {
    return (
      <div className="HomepageUpdated signup-flow">
        <div className="league-content">
          <div className="league-homepage">

            <nav className="homepage-nav">
              <p className="homepage-nav-label">Season {utils.toRomanNumeral(parseInt(CURRENT_SEASON))} Sign Ups</p>
            </nav>
            <hr className="section-divider" />

            <div className="homepage-section">
              <p className="mini-title">Returning Player</p>
              <p className="signup-body-text">New to the league? <a href="/signup/new">Sign up as a new player</a>.</p>
            </div>

            <div className="homepage-section">
              { displayPlayerSearch() }
            </div>
            <hr className="section-divider" />

          </div>
        </div>

        <Footer/>
      </div>
    );
  }
}

export default ReturningPlayer;
